import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  People,
  Visibility,
  TrendingUp,
  BarChart
} from '@mui/icons-material';
import { useAnalytics } from '../../context/AnalyticsContext';

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${secs.toString().padStart(2, '0')}s`;
};

interface VisitorMetricsProps {
  variant?: 'compact' | 'full';
  showTitle?: boolean;
}

const VisitorMetrics: React.FC<VisitorMetricsProps> = ({ 
  variant = 'full',
  showTitle = true 
}) => {
  const { loading, onlineUsers, totalVisits, pageVisits, getSessionDuration } = useAnalytics();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const topPages = useMemo(() => {
    return Object.entries(pageVisits)
      .map(([page, visits]) => ({ page, visits }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 4);
  }, [pageVisits]);

  // Compact version just shows the online users chip
  if (variant === 'compact') {
    return (
      <Tooltip title="Current online users">
        <Chip 
          icon={<People fontSize="small" />} 
          label={`${onlineUsers} online`}
          color="primary"
          size="small"
        />
      </Tooltip>
    );
  }

  // Full version shows all metrics
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      {showTitle && (
        <Typography variant="h6" gutterBottom>
          Visitor Metrics
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Chip 
          icon={<People />} 
          label={`${onlineUsers} online now`}
          color="primary"
          variant="outlined"
        />
        <Chip 
          icon={<Visibility />} 
          label={`${totalVisits} total visits`}
          color="secondary"
          variant="outlined"
        />
        <Chip 
          icon={<TrendingUp />} 
          label={`${formatDuration(getSessionDuration())} session`}
          color="success"
          variant="outlined"
        />
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
        Top Pages
      </Typography>
      
      <List dense>
        {topPages.map((item) => (
          <ListItem key={item.page} disablePadding sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <BarChart fontSize="small" color="action" />
            </ListItemIcon>
            <ListItemText 
              primary={item.page}
              secondary={`${item.visits} visits`}
              primaryTypographyProps={{ variant: 'body2' }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

export default VisitorMetrics;
